import React, { useEffect, useRef, useState } from 'react';
import Head from 'design-system/Head';
import { Extras } from 'react-scale/corp/components/Lookup/LookupTypes';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

import { WithCopy } from '@scale/frontend-lib/sensor-fusion';
import { Typography } from '@scale/scaleui';
import { ISensorFusionSubstageInstanceLookupDto } from '@scale/sensor-fusion-shared';
import { DOUBLE_COLUMN_MIN_WIDTH } from '../constants';

import SubstageInstanceOverviewPanel from './SubstageInstanceOverviewPanel';
import SubstageInstanceActionsPanel from './SubstageInstanceToolsPanel';
import SubstageInstanceJSONPanel from './SubstageInstanceJSONPanel';
import SubstageInstancePipelinePanel from './SubstageInstancePipelinePanel';
import SubstageInstanceResponsePanel from './SubstageInstanceResponsesPanel';
import SubstageInstanceSubmissionPanel from './SubstageInstanceSubmissionPanel';
import SubstageInstanceTaxonomyPanel from './SubstageInstanceTaxonomyPanel';
import SubstageInstanceInternalAuditPanel from './SubstageInstanceInternalAuditPanel';
import SubstageInstanceCustomerAuditPanel from './SubstageInstanceCustomerAuditPanel';

type SubstageInstanceLookupViewProps = {
  extras: Partial<Extras>;
  substageInstance: ISensorFusionSubstageInstanceLookupDto;
};

const SubstageInstanceLookupView = ({
  extras,
  substageInstance,
}: SubstageInstanceLookupViewProps) => {
  const myRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);

  const { isRemoAdmin } = useRemoAdminCtx();

  useEffect(() => {
    const updateWidth = () => {
      setWidth(myRef?.current?.getBoundingClientRect().width);
    };

    window.addEventListener('resize', updateWidth);
    updateWidth();
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const leftColumnElements = (
    <>
      <SubstageInstanceOverviewPanel substageInstance={substageInstance} />
      <SubstageInstanceActionsPanel substageInstance={substageInstance} />
      {!isRemoAdmin && (
        <>
          <SubstageInstanceTaxonomyPanel substageInstance={substageInstance} />
          <SubstageInstanceJSONPanel substageInstance={substageInstance} />
        </>
      )}
    </>
  );

  const rightColumnElements = (
    <>
      <SubstageInstanceSubmissionPanel substageInstance={substageInstance} />
      <SubstageInstancePipelinePanel substageInstance={substageInstance} />
      <SubstageInstanceResponsePanel substageInstance={substageInstance} />
      {!isRemoAdmin && (
        <>
          <SubstageInstanceCustomerAuditPanel extras={extras} substageInstance={substageInstance} />
          <SubstageInstanceInternalAuditPanel substageInstance={substageInstance} />
        </>
      )}
    </>
  );

  return (
    <div
      className="scaleui scale-tailwind scale-theme w-max-full w-full justify-center overflow-scroll px-8"
      ref={myRef}
    >
      <Head
        title={`Substage Instance ${substageInstance._id} ${substageInstance.substageDefinition.name}`}
      />

      <Typography.H1>Substage Instance</Typography.H1>

      <div className="mx-1">
        <WithCopy className="inline-block" copyText={substageInstance._id}>
          {substageInstance._id}
        </WithCopy>
      </div>

      {width && width >= DOUBLE_COLUMN_MIN_WIDTH ? (
        <div className="flex gap-4 min-w-0">
          <div className="flex flex-col gap-4 min-w-0 w-1/3 overflow-wrap-anywhere">
            {leftColumnElements}
          </div>
          <div className="flex flex-col gap-4 min-w-0 w-2/3">{rightColumnElements}</div>
        </div>
      ) : (
        <div className="flex flex-col gap-4 min-w-0">
          {leftColumnElements}
          {rightColumnElements}
        </div>
      )}
      <style jsx>{`
        .overflow-wrap-anywhere {
          overflow-wrap: anywhere;
        }
      `}</style>
    </div>
  );
};

export default SubstageInstanceLookupView;
